/**
 * SignalDetailChart
 * =================
 *
 * The "full timeline" chart shown in the Signal detail dialog. Unlike the
 * grid card (which caps the value chart to the most-recent 100 bins for a
 * clean glance), this view shows the WHOLE history inside a horizontally
 * scrollable container, defaulting scrolled to the right (most recent).
 *
 * The component still accepts ``eventMarkers`` (sentiment-colored dots via
 * ``SignalBinMiniChart``'s event-marker layer), but the detail dialog no
 * longer passes them: this view is about one signal's own data, and
 * workspace-wide event dots read as if they belonged to the signal. The
 * Overview chart is where events and signals are shown together.
 *
 * Event STREAMS (``type === 'event'``) keep their own dot chart in the card
 * below, so this component renders only for time-bucketed signals.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, IconButton, Stack, Tooltip, Typography, useTheme,
} from '@mui/material';
import {
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  CenterFocusWeak as ResetZoomIcon,
} from '@mui/icons-material';
import SignalBinMiniChart from '../../containers/charts/signalBinsMini';

const PX_PER_BIN = 16;     // default horizontal density per time bucket
const PX_MIN = 4;          // most zoomed-out (dense)
const PX_MAX = 64;         // most zoomed-in (spacious)
const CHART_HEIGHT = 240;  // taller than the card mini chart

const _hasTimeBins = (bins) =>
  Array.isArray(bins) && bins.some((b) => b && (b.from !== undefined || b.to !== undefined));

const SignalDetailChart = ({ signal, eventMarkers }) => {
  const theme = useTheme();
  const scrollRef = useRef(null);
  const [pxPerBin, setPxPerBin] = useState(PX_PER_BIN);

  const bins = signal?.bins;
  const isEvent = signal?.type === 'event';
  const showTimeline = !isEvent && _hasTimeBins(bins);

  // Inner width scales with bin count AND the zoom level so dense histories
  // stay readable and the container scrolls instead of cramming everything in.
  const innerWidth = useMemo(() => {
    const n = Array.isArray(bins) ? bins.length : 0;
    return Math.max(0, n) * pxPerBin;
  }, [bins, pxPerBin]);

  const zoomIn = () => setPxPerBin((p) => Math.min(PX_MAX, Math.round(p * 1.5)));
  const zoomOut = () => setPxPerBin((p) => Math.max(PX_MIN, Math.round(p / 1.5)));
  const resetZoom = () => setPxPerBin(PX_PER_BIN);

  // How many events fall within this signal's span (for the caption).
  const eventCount = useMemo(() => {
    const ms = Array.isArray(eventMarkers) ? eventMarkers : [];
    if (!ms.length || !Array.isArray(bins) || bins.length === 0) return 0;
    const lo = bins[0]?.from ? Date.parse(bins[0].from) : NaN;
    const hiRaw = bins[bins.length - 1]?.to || bins[bins.length - 1]?.from;
    const hi = hiRaw ? Date.parse(hiRaw) : NaN;
    if (!Number.isFinite(lo) || !Number.isFinite(hi)) return 0;
    return ms.filter((m) => {
      const t = Number(m?.t);
      return Number.isFinite(t) && t >= lo && t <= hi;
    }).length;
  }, [eventMarkers, bins]);

  // Default the scroll to the most recent (right edge) on open.
  useEffect(() => {
    if (showTimeline && scrollRef.current) {
      scrollRef.current.scrollLeft = scrollRef.current.scrollWidth;
    }
  }, [showTimeline, innerWidth]);

  if (!showTimeline) return null;

  // Whether scrolling is actually needed (only relevant past ~1 screen).
  const fitsWithoutScroll = innerWidth <= 760;

  return (
    <Box
      sx={{
        mb: 2,
        p: 1.5,
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: theme.palette.background.paper,
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: 0.4 }}>
          FULL TIMELINE
        </Typography>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {eventCount > 0 && (
              <Box component="span" sx={{ mr: 1 }}>
                {eventCount} event{eventCount === 1 ? '' : 's'} overlaid
              </Box>
            )}
            {!fitsWithoutScroll && 'scroll ◂▸ for history'}
          </Typography>
          <Stack direction="row" alignItems="center">
            <Tooltip title="Zoom out">
              <span>
                <IconButton size="small" onClick={zoomOut} disabled={pxPerBin <= PX_MIN}>
                  <ZoomOutIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Reset zoom">
              <span>
                <IconButton size="small" onClick={resetZoom} disabled={pxPerBin === PX_PER_BIN}>
                  <ResetZoomIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Zoom in">
              <span>
                <IconButton size="small" onClick={zoomIn} disabled={pxPerBin >= PX_MAX}>
                  <ZoomInIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Stack>
      </Stack>
      <Box
        ref={scrollRef}
        sx={{
          overflowX: fitsWithoutScroll ? 'hidden' : 'auto',
          overflowY: 'hidden',
          '&::-webkit-scrollbar': { height: 8 },
          '&::-webkit-scrollbar-thumb': {
            background: theme.palette.divider,
            borderRadius: 4,
          },
        }}
      >
        <Box sx={{ width: fitsWithoutScroll ? '100%' : innerWidth, minWidth: '100%' }}>
          {/* maxPoints=Infinity => show the WHOLE history (no 100-cap). */}
          <SignalBinMiniChart
            data={bins}
            eventMarkers={eventMarkers}
            height={CHART_HEIGHT}
            maxPoints={Infinity}
            unit={signal?.unit}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default SignalDetailChart;
